import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  // Exchange Emergent session_id for session_token
  exchangeSession: async (sessionId) => {
    const response = await api.post('/auth/session', {}, {
      headers: { 'X-Session-ID': sessionId },
    });
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    return response.data;
  },

  // Register with email/password
  register: async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    return response.data;
  },

  // Login with email/password
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    return response.data;
  },

  // Get current user
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('session_token');
    }
  },
};

// Newsletter API
export const newsletterAPI = {
  // Get usage limit
  getLimit: async () => {
    const response = await api.get('/newsletters/limit');
    return response.data;
  },

  // List all newsletters
  list: async () => {
    const response = await api.get('/newsletters');
    return response.data;
  },

  // Create newsletter
  create: async (title, templateId, content) => {
    const response = await api.post('/newsletters', {
      title,
      template_id: templateId,
      content,
    });
    return response.data;
  },

  // Get single newsletter
  get: async (newsletterId) => {
    const response = await api.get(`/newsletters/${newsletterId}`);
    return response.data;
  },

  // Update newsletter
  update: async (newsletterId, data) => {
    const response = await api.put(`/newsletters/${newsletterId}`, data);
    return response.data;
  },

  // Delete newsletter
  delete: async (newsletterId) => {
    const response = await api.delete(`/newsletters/${newsletterId}`);
    return response.data;
  },

  // Generate PDF
  generatePDF: async (newsletterId) => {
    const response = await api.post(`/newsletters/${newsletterId}/pdf`, {}, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Document Upload API
export const uploadAPI = {
  // Upload document for content extraction
  extractContent: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload custom PDF template
  uploadTemplate: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get custom template PDF URL
  getTemplateUrl: (templateId) => {
    const token = localStorage.getItem('session_token');
    return `${API}/templates/${templateId}/pdf?token=${token}`;
  },
};

export default api;
